import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Play, Bookmark, Share2, Calendar, ChevronRight, ArrowUpDown, BookOpen } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import { BINGE_SERIES, loadResume } from "../lib/binge";
import { Navbar } from "../components/Navbar";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";

export default function SeriesDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [asc, setAsc] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/series/${slug}`)
      .then((res) => setSeries(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading)
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );

  if (notFound || !series)
    return (
      <div className="min-h-screen bg-[#0B0C10]">
        <Navbar />
        <div className="text-center py-32">
          <p className="font-display text-2xl text-white">Series not found</p>
          <Link to="/" className="text-rose-400 mt-4 inline-block">← Back to library</Link>
        </div>
      </div>
    );

  const chapters = [...(series.chapters || [])].sort((a, b) =>
    asc ? a.chapter_number - b.chapter_number : b.chapter_number - a.chapter_number
  );
  const firstChapter = series.chapters && series.chapters[0];

  const share = () => {
    navigator.clipboard?.writeText(window.location.href);
    toast.success("Link copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-[#0B0C10]">
      <Navbar />

      {/* Banner */}
      <div className="relative h-[38vh] sm:h-[46vh] overflow-hidden">
        <img
          src={resolveImg(series.banner_image_url || series.cover_image_url)}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/60 to-[#0B0C10]/20" />
        <Link
          to="/"
          data-testid="back-to-library-btn"
          className="absolute top-5 left-5 inline-flex items-center gap-2 text-sm text-white bg-black/50 backdrop-blur border border-white/10 px-4 py-2 rounded-full hover:bg-black/70 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Library
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-24 relative z-10 pb-24">
        <div className="flex flex-col sm:flex-row gap-7">
          {/* Poster */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="shrink-0 mx-auto sm:mx-0"
          >
            <div className="w-44 sm:w-56 aspect-[3/4] rounded-2xl overflow-hidden border border-white/15 shadow-2xl glow-primary">
              <img src={resolveImg(series.cover_image_url)} alt={series.title} className="w-full h-full object-cover" />
            </div>
          </motion.div>

          {/* Info */}
          <div className="flex-1 pt-2 sm:pt-24">
            <div className="flex items-center gap-3 flex-wrap">
              <StatusBadge status={series.status} />
              <span className="font-mono-kh text-xs uppercase tracking-widest text-gray-400">{series.genre}</span>
            </div>
            <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight text-white mt-3 leading-none">
              {series.title}
            </h1>
            {series.author && (
              <p className="text-sm text-gray-400 mt-2">by <span className="text-gray-200">{series.author}</span></p>
            )}
            <p className="text-base text-gray-300 leading-relaxed mt-5 max-w-2xl">{series.synopsis}</p>

            <div className="flex items-center gap-3 mt-7 flex-wrap">
              <Button
                data-testid="start-reading-btn"
                disabled={!firstChapter}
                onClick={() => firstChapter && navigate(`/series/${series.slug}/chapter/${firstChapter.chapter_number}`)}
                className="bg-rose-600 hover:bg-rose-500 text-white font-semibold rounded-full px-6 h-11 glow-primary"
              >
                <Play className="w-4 h-4 mr-2" /> {firstChapter ? `Start Ch ${formatNum(firstChapter.chapter_number)}` : "No chapters yet"}
              </Button>
              {BINGE_SERIES.includes(series.slug) && firstChapter && (
                <BingeButtons slug={series.slug} chapters={series.chapters} />
              )}
              <Button
                data-testid="bookmark-btn"
                onClick={() => toast.success("Bookmarked")}
                variant="outline"
                className="border-white/15 bg-transparent text-gray-200 hover:bg-white/5 rounded-full h-11"
              >
                <Bookmark className="w-4 h-4 mr-2" /> Bookmark
              </Button>
              <Button
                data-testid="share-btn"
                onClick={share}
                variant="ghost"
                className="text-gray-300 hover:bg-white/5 rounded-full h-11 w-11 p-0"
              >
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Chapter list */}
        <div className="mt-14">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-display font-bold text-xl sm:text-2xl text-white">
              Chapters <span className="text-gray-500 text-base font-mono-kh">({chapters.length})</span>
            </h2>
            <button
              data-testid="sort-chapters-btn"
              onClick={() => setAsc((v) => !v)}
              className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white bg-white/5 border border-white/10 px-3.5 py-2 rounded-full transition-colors"
            >
              <ArrowUpDown className="w-3.5 h-3.5" /> {asc ? "Oldest first" : "Newest first"}
            </button>
          </div>

          {chapters.length === 0 ? (
            <div className="text-center py-16 text-gray-500 font-mono-kh border border-dashed border-white/10 rounded-2xl">
              No published chapters yet.
            </div>
          ) : (
            <div className="space-y-2">
              {chapters.map((c) => (
                <Link
                  key={c.id}
                  to={`/series/${series.slug}/chapter/${c.chapter_number}`}
                  data-testid={`chapter-item-${formatNum(c.chapter_number)}`}
                  className="group flex items-center justify-between gap-4 bg-[#12141D] hover:bg-[#1A1D2B] border border-white/10 hover:border-rose-500/30 rounded-xl px-4 sm:px-5 py-4 transition-all"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    {c.thumbnail_url ? (
                      <img
                        src={resolveImg(c.thumbnail_url)}
                        alt=""
                        loading="lazy"
                        className="w-12 h-12 rounded-lg object-cover shrink-0 border border-white/10"
                      />
                    ) : (
                      <span className="font-display font-bold text-rose-500 text-lg w-14 shrink-0 text-center">
                        {formatNum(c.chapter_number)}
                      </span>
                    )}
                    <div className="min-w-0">
                      <p className="text-white font-medium truncate group-hover:text-rose-400 transition-colors">
                        <span className="text-rose-500 font-display font-bold mr-2">{formatNum(c.chapter_number)}</span>
                        {c.title || `Chapter ${formatNum(c.chapter_number)}`}
                      </p>
                      {c.publish_date && (
                        <p className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                          <Calendar className="w-3 h-3" /> {new Date(c.publish_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-rose-500 group-hover:translate-x-1 transition-all shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BingeButtons({ slug, chapters }) {
  const navigate = useNavigate();
  const saved = loadResume(slug);
  const canResume = saved && chapters.some((c) => c.chapter_number === saved.chapter);
  return (
    <>
      <Button
        data-testid="read-from-beginning-btn"
        onClick={() => navigate(`/series/${slug}/all`)}
        variant="outline"
        className="border-rose-500/40 bg-transparent text-white hover:bg-rose-500/10 rounded-full h-11"
      >
        <BookOpen className="w-4 h-4 mr-2" /> Read from the beginning
      </Button>
      {canResume && (
        <Button
          data-testid="continue-reading-btn"
          onClick={() => navigate(`/series/${slug}/all?resume=1`)}
          className="bg-[#e61c5d] hover:bg-[#ff2a6d] text-white font-semibold rounded-full px-6 h-11"
        >
          Continue reading — Episode {formatNum(saved.chapter)}
        </Button>
      )}
    </>
  );
}

function formatNum(n) {
  return Number.isInteger(n) ? String(n) : String(n);
}
